import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useToast } from "./use-toast";
import { isNativeIosApp } from "@/lib/platform";

type Staff = Tables<"staff">;
type StaffInsert = TablesInsert<"staff">;
type StaffUpdate = TablesUpdate<"staff">;

interface StaffWithLocations extends Staff {
  assignedLocationIds?: string[];
}

type StaffLocationAssignment = {
  staff_id: string;
  location_id: string;
};

export function useStaff() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const isNativeIos = isNativeIosApp();
  const [staff, setStaff] = useState<StaffWithLocations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStaff = useCallback(async () => {
    if (!business) {
      setStaff([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch staff
      const { data: staffData, error: fetchError } = await supabase
        .from("staff")
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      // Fetch location assignments
      const { data: staffLocations, error: locationsError } = await supabase
        .from("staff_locations" as any)
        .select("staff_id, location_id");

      if (locationsError) throw locationsError;

      // Map locations to staff
      const staffWithLocations = (staffData || []).map((member) => ({
        ...member,
        assignedLocationIds: ((staffLocations || []) as unknown as StaffLocationAssignment[])
          ?.filter((sl) => sl.staff_id === member.id)
          .map((sl) => sl.location_id) || [],
      }));

      setStaff(staffWithLocations);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch staff");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const createStaff = async (
    staffData: Omit<StaffInsert, "business_id">,
    locationIds?: string[]
  ) => {
    if (!business) throw new Error("No business found");

    // Friendly client-side limit check (DB triggers also enforce)
    const planId = (business as any).plan_id || "starter";
    const { data: plan, error: planError } = await supabase
      .from("subscription_plans")
      .select("staff_limit")
      .eq("id", planId)
      .maybeSingle();
    if (planError) throw planError;
    const staffLimit = (plan as any)?.staff_limit ?? null;
    if (staffLimit != null) {
      const { data: staffRows, error: countError } = await supabase
        .from("staff")
        .select("id")
        .eq("business_id", business.id)
        .eq("active", true);
      if (countError) throw countError;
      const current = staffRows?.length || 0;
      if (current >= staffLimit) {
        toast({
          title: "Staff limit reached",
          description: isNativeIos
            ? "Please contact Buukd support for help adding more staff."
            : `Your plan allows up to ${staffLimit} staff. Upgrade to add more.`,
          variant: "destructive",
        });
        throw new Error("Staff limit reached");
      }
    }

    const { data, error } = await supabase
      .from("staff")
      .insert({ ...staffData, business_id: business.id })
      .select()
      .single();

    if (error) {
      const msg = (error as any)?.message || "Failed to add staff";
      if (msg.toLowerCase().includes("staff limit reached")) {
        toast({
          title: "Staff limit reached",
          description: isNativeIos
            ? "Please contact Buukd support for help adding more staff."
            : "Upgrade your plan to add more staff.",
          variant: "destructive",
        });
      }
      throw error;
    }

    // Assign locations if provided (empty array = available at all locations, so don't insert)
    if (locationIds && locationIds.length > 0 && data) {
      const locationAssignments = locationIds.map((locationId) => ({
        staff_id: data.id,
        location_id: locationId,
      }));
      await supabase.from("staff_locations" as any).insert(locationAssignments as any);
    }

    toast({ title: "Staff added", description: `${data.name} has been added.` });
    await fetchStaff();
    return data;
  };

  const updateStaff = async (
    id: string,
    staffData: StaffUpdate,
    locationIds?: string[]
  ) => {
    const { data, error } = await supabase
      .from("staff")
      .update(staffData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    // Update location assignments
    if (locationIds !== undefined) {
      await supabase.from("staff_locations" as any).delete().eq("staff_id", id);
      // Empty array = available at all locations, so don't insert anything
      if (locationIds.length > 0) {
        const locationAssignments = locationIds.map((locationId) => ({
          staff_id: id,
          location_id: locationId,
        }));
        await supabase.from("staff_locations" as any).insert(locationAssignments as any);
      }
    }

    toast({ title: "Staff updated", description: `${data.name} has been updated.` });
    await fetchStaff();
    return data;
  };

  const deleteStaff = async (id: string) => {
    const { error } = await supabase
      .from("staff")
      .update({ active: false })
      .eq("id", id);

    if (error) throw error;
    toast({ title: "Staff removed", description: "Staff member has been deactivated." });
    await fetchStaff();
  };

  return {
    staff,
    loading,
    error,
    createStaff,
    updateStaff,
    deleteStaff,
    refetch: fetchStaff,
  };
}
